import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useActionRequest } from "../../../../common/hooks/api";
import { Api } from "../../../../common/service/Api";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../../common/service/server/Types";
import { usePrimaryOrganizationId } from "../../../hooks/useOrganization";
import { isUrl } from "../../../util/validation";
import { DataRecipientLogo } from "../../DataRecipientLogo";
import { RadioWithFileReference } from "./RadioWithFileReference";
import { RadioWithTextField } from "./RadioWithTextField";
import { RadioWithDocumentInput } from "./RadioWithDocumentInput";
import { DocumentationInputValue } from "./DocumentationInput";
import { Disableable } from "../../Disableable";
import { RadioGroup as AquiloRadioGroup } from "@truevault/aquilo";

type Props = {
  recipients: OrganizationDataRecipientDto[];
  options: VendorRadioOption[];

  onFlagChanged: (
    recipient: OrganizationDataRecipientDto,
    flag: VendorFlagOption,
    key?: string,
  ) => Promise<void>;

  onUrlChanged: (
    recipient: OrganizationDataRecipientDto,
    url: string,
    key?: string,
  ) => Promise<void>;

  onFileChanged: (
    recipient: OrganizationDataRecipientDto,
    filename: string,
    filekey: string,
    key?: string,
  ) => Promise<void>;

  getFlag: (recipient: OrganizationDataRecipientDto) => VendorFlagOption;
  getUrl: (recipient: OrganizationDataRecipientDto) => string | undefined;
  getFileName: (recipient: OrganizationDataRecipientDto) => string | undefined;
  getFileKey: (recipient: OrganizationDataRecipientDto) => string | undefined;

  getIsVendorRadioGroupDisabled?: (recipient: OrganizationDataRecipientDto) => boolean;
  radioGroupDisabledTooltip?: string;

  onTextChanged?: (
    recipient: OrganizationDataRecipientDto,
    url: string,
    key?: string,
  ) => Promise<void>;
  onTextCleared?: (recipient: OrganizationDataRecipientDto) => string;
  onDocumentChanged?: (
    recipient: OrganizationDataRecipientDto,
    value: DocumentationInputValue,
    prevValue: DocumentationInputValue,
  ) => Promise<void>;
};

export const VendorFlagWithDocumentation = ({
  recipients,
  options,

  onFlagChanged,
  onUrlChanged,
  onFileChanged,
  onTextChanged,
  onTextCleared,
  onDocumentChanged,

  getFlag,
  getUrl,
  getFileName,
  getFileKey,
  getIsVendorRadioGroupDisabled,
  radioGroupDisabledTooltip,
}: Props) => {
  const orgId = usePrimaryOrganizationId();

  //
  // Handlers
  //

  const { fetch: postVendorAgreement } = useActionRequest({
    api: async ({ vendorId, filename, filedata, key }) => {
      const result = await Api.storage.postOrganizationVendorAgreement(
        orgId,
        vendorId,
        filename,
        filedata,
      );

      if (!result.error && result.vendorId) {
        const r = recipients.find((r) => r.vendorId == result.vendorId);
        await onFileChanged(r, result.fileName, result.fileKey, key);
      }
    },
    messages: {
      forceError: "Failed to upload vendor agreement",
      success: "Uploaded vendor agreement",
    },
  });

  const { fetch: deleteVendorAgreement } = useActionRequest({
    api: async ({ vendorId, filekey, key }) => {
      const result = await Api.storage.deleteOrganizationVendorAgreement(orgId, vendorId, filekey);
      if (!result.error && result.vendorId) {
        const r = recipients.find((r) => r.vendorId == result.vendorId);
        await onFileChanged(r, result.fileName, result.fileKey, key);
      }
    },
    messages: {
      forceError: "Failed to delete vendor agreement",
      success: "Deleted vendor agreement",
    },
  });

  const handleFileUpload = async (
    vendorId: string,
    filename: string,
    filedata: File,
    key?: string,
  ) => postVendorAgreement({ vendorId, filename, filedata, key });

  const handleFileDelete = (vendorId: string, filekey: string, key?: string) =>
    deleteVendorAgreement({ vendorId, filekey, key });

  return (
    <div className="classify-vendors--container">
      <div className="classify-vendors--vendors-container">
        {recipients
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((r) => {
            return (
              <VendorRow
                key={r.id}
                options={options}
                recipient={r}
                selected={getFlag(r)}
                url={getUrl(r)}
                fileName={getFileName(r)}
                fileKey={getFileKey(r)}
                radioGroupDisabled={
                  getIsVendorRadioGroupDisabled ? getIsVendorRadioGroupDisabled(r) : false
                }
                radioGroupDisabledTooltip={radioGroupDisabledTooltip || ""}
                onFlagChanged={(flag, key) => onFlagChanged(r, flag, key)}
                onUrlChanged={(url, key) => onUrlChanged(r, url, key)}
                handleFileUpload={handleFileUpload}
                handleFileDelete={handleFileDelete}
                onTextChanged={(value, key) => onTextChanged(r, value, key)}
                onTextCleared={() => {
                  if (onTextCleared) {
                    return onTextCleared(r);
                  }
                  return "";
                }}
                onDocumentChanged={(value, prevValue) => onDocumentChanged(r, value, prevValue)}
              />
            );
          })}
      </div>
    </div>
  );
};

type VendorRowProps = {
  recipient: OrganizationDataRecipientDto;

  options: VendorRadioOption[];

  selected: VendorFlagOption;
  url?: string;
  fileName?: string;
  fileKey?: string;

  radioGroupDisabled?: boolean;
  radioGroupDisabledTooltip?: string;

  onFlagChanged: (selected: VendorFlagOption, key?: string) => Promise<void>;
  onUrlChanged: (url: string, key?: string) => Promise<void>;
  handleFileUpload: (
    vendorId: UUIDString,
    fileName: string,
    fileData: File,
    key?: string,
  ) => Promise<void>;
  handleFileDelete: (vendorId: UUIDString, fileKey: string, key?: string) => Promise<void>;
  onTextChanged?: (value: string, key?: string) => Promise<void>;
  onTextCleared?: () => string;
  onDocumentChanged?: (
    value: DocumentationInputValue,
    prevValue: DocumentationInputValue,
  ) => Promise<void>;
};
const VendorRow = ({
  recipient,
  options,
  radioGroupDisabled,
  radioGroupDisabledTooltip,

  selected,
  url,
  fileName,
  fileKey,

  onFlagChanged,
  onUrlChanged,
  handleFileUpload,
  handleFileDelete,
  onTextChanged,
  onTextCleared,
  onDocumentChanged,
}: VendorRowProps) => {
  return (
    <div className="data-recipient-radio-options">
      <div className="data-recipient-radio-options__logo">
        <DataRecipientLogo dataRecipient={recipient} size={24} />
      </div>

      <div className="data-recipient-radio-options__info">
        <div className="classify-vendors--vendor-name">{recipient.name}</div>
      </div>

      <VendorRadioSection
        recipient={recipient}
        value={selected}
        options={options}
        url={url}
        fileName={fileName}
        fileKey={fileKey}
        onSelectionChange={onFlagChanged}
        onUrlChange={onUrlChanged}
        onFileUpload={(filename, filedata, key) =>
          handleFileUpload(recipient.vendorId, filename, filedata, key)
        }
        onFileDelete={(fileKey, optionKey) =>
          handleFileDelete(recipient.vendorId, fileKey, optionKey)
        }
        onTextChanged={onTextChanged}
        onTextCleared={onTextCleared}
        onDocumentChanged={onDocumentChanged}
        disabled={radioGroupDisabled}
        disabledTooltip={radioGroupDisabledTooltip}
      />
    </div>
  );
};

export type VendorFlagOption = string | number | null;

type LabelFn = (recipient: OrganizationDataRecipientDto) => string | React.ReactNode;

const resolveOptionLabel = (
  option: VendorRadioOption,
  recipient: OrganizationDataRecipientDto,
): React.ReactNode => {
  if (option.dynamic) {
    return (option.label as LabelFn)(recipient);
  }

  return option.label as React.ReactNode;
};

export type VendorRadioOption<
  TValue extends VendorFlagOption = VendorFlagOption,
  TKey extends string = string,
> = {
  key: TKey;
  label: string | React.ReactNode | LabelFn;
  value: TValue;
  type: "file" | "normal" | "text" | "document";
  dynamic?: boolean;
  textLabel?: string;
  placeholder?: string;
  disabled?: boolean | ((recipient: OrganizationDataRecipientDto) => boolean);
  disabledTooltip?: string;
  visible?: boolean;
  isOptional?: boolean | ((recipient: OrganizationDataRecipientDto) => boolean);
  getTextValue?: (recipient: OrganizationDataRecipientDto) => string;
  createFileLink?: (recipient: OrganizationDataRecipientDto, fileKey: string) => string;
};

type VendorRadioSectionProps = {
  recipient: OrganizationDataRecipientDto;
  options: VendorRadioOption[];
  value: VendorFlagOption;
  url?: string;
  fileName?: string;
  fileKey?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  hideDocumentationInput?: boolean;
  onSelectionChange: (option: VendorFlagOption, key?: string) => Promise<void>;
  onUrlChange: (vendorContractUrl: string, key?: string) => Promise<void>;
  onFileUpload: (fileName: string, fileData: File, key?: string) => Promise<void>;
  onFileDelete: (fileKey: string, key?: string) => Promise<void>;
  onTextChanged?: (value: string, key?: string) => Promise<void>;
  onTextCleared?: () => string;
  onDocumentChanged?: (
    value: DocumentationInputValue,
    prevValue: DocumentationInputValue,
  ) => Promise<void>;
  forceShowOptional?: boolean;
  useAquiloDesign?: boolean;
  hideSelectionRadioIfOnlyOneOption?: boolean;
};

export const VendorRadioSection = ({
  recipient,
  options,
  value,
  url,
  fileName,
  fileKey,
  hideDocumentationInput = false,
  onSelectionChange,
  onUrlChange,
  onFileUpload,
  onFileDelete,
  onTextChanged,
  onTextCleared,
  onDocumentChanged,
  disabled = false,
  disabledTooltip,
  forceShowOptional = false,
  useAquiloDesign = false,
  hideSelectionRadioIfOnlyOneOption = false, // if true, and the number of visible `options` is exactly 1, then the radio select button will be hidden (but the extra content will still be shown)
}: VendorRadioSectionProps) => {
  const [initialFileValue, setInitialFileValue] = useState<DocumentationInputValue | null>(null);
  useEffect(() => {
    if (recipient.gdprSccUrl) {
      setInitialFileValue({ type: "url", url: recipient.gdprSccUrl });
    } else if (recipient.gdprSccFileName) {
      setInitialFileValue({
        type: "file",
        fileName: recipient.gdprSccFileName,
        fileKey: recipient.gdprSccFileKey,
      });
    }
  }, [recipient]);

  const errorMessage = useMemo(
    () => (url && !isUrl(url) ? "Please provide a valid URL" : null),
    [url],
  );

  const [loading, setLoading] = useState(false);
  const [localSelection, setLocalSelection] = useState(value);
  const [fileError, setFileError] = useState({});
  const visibleOptions = useMemo(() => options.filter((o) => o.visible !== false), [options]);

  useEffect(() => {
    setLocalSelection(value);
  }, [value]);

  const handleSelectionChange = async ({ target }) => {
    setLoading(true);
    try {
      const selectedOption = options.find((option) => option.value === target.value);
      setLocalSelection(selectedOption.value);
      await onSelectionChange(selectedOption.value, selectedOption.key);
    } finally {
      setLoading(false);
    }
  };

  const handleUrlChange = async (newUrl: string, key: string) => {
    setLoading(true);
    try {
      await onUrlChange(newUrl, key);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, key: string) => {
    const file = e.target.files[0];
    if (file && file.size <= 25000000) {
      setLoading(true);
      setFileError((error) => ({ ...error, [key]: null }));
      try {
        await onFileUpload(file.name, file, key);
      } finally {
        setLoading(false);
      }
    } else {
      setFileError((error) => ({ ...error, [key]: "(max file size: 25MB)" }));
    }
  };

  const handleFileDelete = async () => {
    setLoading(true);
    try {
      await onFileDelete(fileKey);
    } finally {
      setLoading(false);
    }
  };

  // the ones that are only used for the actual radio options are: label, value, onRadioChanged, loading
  let radioGroupOptions = visibleOptions.map((o) => {
    const label = resolveOptionLabel(o, recipient);
    let isDisabled = disabled || loading || o.disabled === true;
    if (!isDisabled && typeof o.disabled === "function") {
      isDisabled = o.disabled(recipient);
    }
    let isOptional = o.isOptional === true;
    if (!isOptional && typeof o.isOptional === "function") {
      isOptional = o.isOptional(recipient);
    }
    if (o.type === "file") {
      let hideURL = hideDocumentationInput || localSelection !== o.value;

      let radioElem = (
        <RadioWithFileReference
          key={o.key}
          error={errorMessage ?? fileError[o.key]}
          url={url}
          onUrlChanged={(newUrl) => handleUrlChange(newUrl, o.key)}
          onFileChanged={(e) => handleFileUpload(e, o.key)}
          onFileDeleted={handleFileDelete}
          filename={fileName}
          label={label}
          value={o.value}
          hideUrlInput={hideURL}
          disabled={isDisabled}
          loading={loading}
          optional={isOptional || forceShowOptional}
          useAquiloDesign={useAquiloDesign}
        />
      );

      if (useAquiloDesign) {
        return {
          label: label,
          value: o.value,
          disabled: isDisabled || loading,
          extraContent: hideURL ? undefined : radioElem,
        };
      }

      return (
        <Disableable
          key={o.key}
          disabled={isDisabled}
          disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
        >
          {radioElem}
        </Disableable>
      );
    } else if (onTextChanged && o.type === "text") {
      let radioElem = (
        <RadioWithTextField
          key={o.key}
          value={o.value}
          label={label}
          textLabel={o.textLabel ?? ""}
          placeholder={o.placeholder ?? ""}
          selected={o.value === localSelection}
          textValue={o.getTextValue ? o.getTextValue(recipient) : ""}
          onTextChanged={(value) => onTextChanged(value, o.key)}
          onTextCleared={onTextCleared}
          disabled={isDisabled}
          useAquiloDesign={useAquiloDesign}
        />
      );

      if (useAquiloDesign) {
        return {
          label: radioElem, // note that <RadioWithTextField> only returns its label for useAquiloDesign
          value: o.value,
          disabled: isDisabled,
        };
      }

      return (
        <Disableable
          key={o.key}
          disabled={isDisabled}
          disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
        >
          {radioElem}
        </Disableable>
      );
    } else if (onDocumentChanged && o.type === "document") {
      // Since the document type doesn't seem to be used anymore, don't implement it in Aquilo
      if (useAquiloDesign) {
        return null;
      }

      return (
        <Disableable
          key={o.key}
          disabled={isDisabled}
          disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
        >
          <RadioWithDocumentInput
            key={o.key}
            value={o.value}
            label={label}
            selected={o.value === localSelection}
            onDocumentChanged={onDocumentChanged}
            disabled={isDisabled}
            initialValue={initialFileValue}
            createFileLink={(fileKey) => o.createFileLink(recipient, fileKey)}
          />
        </Disableable>
      );
    }

    if (useAquiloDesign) {
      return {
        label: label,
        value: o.value,
        disabled: isDisabled,
      };
    }

    return (
      <Disableable
        key={o.key}
        disabled={isDisabled}
        disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
      >
        <FormControlLabel
          key={o.key}
          value={o.value}
          control={<Radio disabled={isDisabled} color="primary" />}
          label={label}
          disabled={isDisabled}
        />
      </Disableable>
    );
  });

  if (useAquiloDesign) {
    radioGroupOptions = radioGroupOptions.filter((o) => o !== null);

    // If there is only one option, no need for the radio group since the selection is always the same
    if (
      hideSelectionRadioIfOnlyOneOption &&
      radioGroupOptions.length === 1 &&
      "extraContent" in radioGroupOptions[0]
    ) {
      return radioGroupOptions[0].extraContent;
    }

    return (
      <div className="tw-boundary">
        <AquiloRadioGroup
          // @ts-ignore
          selectedValue={localSelection}
          onChange={(newVal: string) => handleSelectionChange({ target: { value: newVal } })}
          options={radioGroupOptions.map((radioOption: any) => {
            return {
              label: radioOption.label,
              value: radioOption.value,
              disabled: radioOption.disabled,
              extraContent: radioOption.extraContent,
            };
          })}
          onlyShowExtraContentForSelectedOption={true}
        />
      </div>
    );
  }

  return (
    <div className="data-recipient-radio-options__options">
      <RadioGroup value={localSelection} onChange={handleSelectionChange} className="w-100">
        {visibleOptions.map((o) => {
          const label = resolveOptionLabel(o, recipient);
          let isDisabled = disabled || loading || o.disabled === true;
          if (!isDisabled && typeof o.disabled === "function") {
            isDisabled = o.disabled(recipient);
          }
          let isOptional = o.isOptional === true;
          if (!isOptional && typeof o.isOptional === "function") {
            isOptional = o.isOptional(recipient);
          }
          if (o.type === "file") {
            let hideURL = hideDocumentationInput || localSelection !== o.value;
            return (
              <Disableable
                key={o.key}
                disabled={isDisabled}
                disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
              >
                <RadioWithFileReference
                  key={o.key}
                  error={errorMessage ?? fileError[o.key]}
                  url={url}
                  onUrlChanged={(newUrl) => handleUrlChange(newUrl, o.key)}
                  onFileChanged={(e) => handleFileUpload(e, o.key)}
                  onFileDeleted={handleFileDelete}
                  filename={fileName}
                  label={label}
                  value={o.value}
                  hideUrlInput={hideURL}
                  disabled={isDisabled}
                  loading={loading}
                  optional={isOptional || forceShowOptional}
                />
              </Disableable>
            );
          } else if (onTextChanged && o.type === "text") {
            return (
              <Disableable
                key={o.key}
                disabled={isDisabled}
                disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
              >
                <RadioWithTextField
                  key={o.key}
                  value={o.value}
                  label={label}
                  textLabel={o.textLabel ?? ""}
                  placeholder={o.placeholder ?? ""}
                  selected={o.value === localSelection}
                  textValue={o.getTextValue ? o.getTextValue(recipient) : ""}
                  onTextChanged={(value) => onTextChanged(value, o.key)}
                  onTextCleared={onTextCleared}
                  disabled={isDisabled}
                />
              </Disableable>
            );
          } else if (onDocumentChanged && o.type === "document") {
            return (
              <Disableable
                key={o.key}
                disabled={isDisabled}
                disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
              >
                <RadioWithDocumentInput
                  key={o.key}
                  value={o.value}
                  label={label}
                  selected={o.value === localSelection}
                  onDocumentChanged={onDocumentChanged}
                  disabled={isDisabled}
                  initialValue={initialFileValue}
                  createFileLink={(fileKey) => o.createFileLink(recipient, fileKey)}
                />
              </Disableable>
            );
          }
          return (
            <Disableable
              key={o.key}
              disabled={isDisabled}
              disabledTooltip={loading ? undefined : disabledTooltip || o.disabledTooltip}
            >
              <FormControlLabel
                key={o.key}
                value={o.value}
                control={<Radio disabled={isDisabled} color="primary" />}
                label={label}
                disabled={isDisabled}
              />
            </Disableable>
          );
        })}
      </RadioGroup>
    </div>
  );
};
